import React, { useCallback, useEffect, useRef, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import {
  Loader,
  Printer,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  Package,
  Barcode,
  Tags,
  Filter,
  FileText,
  History,
  RotateCcw,
  Clock,
  XCircle,
  FileSpreadsheet,
  AlertCircle,
  Layers,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { useSelector } from "react-redux";

import AddProducts from "./AddProducts";
import Pagination from "../../Pagination";
import PrintBarcodeComponent from "./PrintBarcodeComponent";
import PrintSelectionModal from "./PrintSelectionModal";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Bulkproductpage from "../../ProductBulkUpload/Bulkproductpage";
import ManageBomModal from "./ManageBomModal";
import { domain, domainAccounting } from "../../../../security";
import noImage from "../../../../Images/noImage.jpg";
import { selectUserID } from "../../../../redux/IchthusSlice";

const formatDate = (d) =>
  d ? format(new Date(d), "MMM dd, yyyy hh:mm a") : "—";

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50  text-amber-600  border-amber-100",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4 flex items-center gap-4">
      <div className={`p-2.5 rounded-xl border ${colors[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          {label}
        </p>
        <p className="text-2xl font-bold text-slate-800 leading-tight">
          {value}
        </p>
      </div>
    </div>
  );
};

const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [auditItem, setAuditItem] = useState(null);
  const PER_PAGE = 6;

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${domain}/api/Products/history?search=${search}`,
      );
      setHistoryData(res.data);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);
  if (!isOpen) return null;

  const start = (currentPage - 1) * PER_PAGE;
  const current = historyData.slice(start, start + PER_PAGE);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-xl">
              <Trash2 size={18} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">
                Deleted Products
              </h2>
              <p className="text-xs text-slate-400">
                {historyData.length} archived records
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
          >
            <XCircle size={20} />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-slate-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or item code…"
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-red-300 transition"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="animate-spin text-slate-300" size={32} />
            </div>
          ) : historyData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <History size={32} className="mb-3 opacity-40" />
              <p className="text-sm">No archived records found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {current.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-2xl transition group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Package size={14} className="text-red-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-700 truncate">
                          {item.productName}
                        </p>
                        <button
                          onClick={() => setAuditItem(item)}
                          className="text-amber-400 hover:text-amber-600 transition flex-shrink-0"
                          title="Audit trail"
                        >
                          <Clock size={13} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400 font-mono">
                        {item.itemCode}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                    <div className="hidden sm:block text-right">
                      <p className="text-xs font-medium text-red-500">
                        {item.deletedByFullName || "Unknown"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDate(item.deletedDate)}
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        await onRestore(item.id);
                        fetchHistory();
                      }}
                      className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition"
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {historyData.length > PER_PAGE && (
          <div className="px-6 py-4 border-t border-slate-100">
            <Pagination
              itemsPerPage={PER_PAGE}
              totalItems={historyData.length}
              currentPage={currentPage}
              paginate={setCurrentPage}
            />
          </div>
        )}
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.productName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

const DescriptionModal = ({ text, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
        <h3 className="font-bold text-slate-800 flex items-center gap-2">
          <FileText size={16} className="text-slate-400" /> Product Description
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition"
        >
          <X size={18} />
        </button>
      </div>
      <div className="p-6">
        <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
          {text}
        </p>
      </div>
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 shadow-sm transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const AllProducts = () => {
  const currentUserID = useSelector(selectUserID);

  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productToEdit, setProductToEdit] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [auditItem, setAuditItem] = useState(null);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [selectedProductsForPrint, setSelectedProductsForPrint] = useState([]);
  const [barcodeToPrint, setBarcodeToPrint] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);

  // New state for Item Type filtering
  const [itemTypeFilter, setItemTypeFilter] = useState("");
  const [filteredByItemType, setFilteredByItemType] = useState([]);
  const [isItemTypeLoading, setIsItemTypeLoading] = useState(false);

  const printBarcodeRef = useRef();
  const ITEMS_PER_PAGE = 10;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/Products`);
      const data = res.data.map((item) => ({ ...item, productImage: noImage }));
      setProductData(data);
      // If not filtered by item type, update filteredProducts
      if (!itemTypeFilter) {
        setFilteredProducts(data);
      }
    } catch {
      toast.error("Failed to fetch products.");
    } finally {
      setLoading(false);
    }
  }, [itemTypeFilter]);

  // Fetch products by ItemType Category
  const fetchProductsByItemTypeCategory = useCallback(async (category = "") => {
    setIsItemTypeLoading(true);
    try {
      const url = category
        ? `${domain}/api/Products/by-item-type-category?category=${category}`
        : `${domain}/api/Products/by-item-type-category`;
      const res = await axios.get(url);
      const data = Array.isArray(res.data) ? res.data : [];
      const dataWithImages = data.map((item) => ({
        ...item,
        productImage: noImage,
      }));
      setFilteredByItemType(dataWithImages);
      return dataWithImages;
    } catch (error) {
      console.error("Failed to fetch products by type:", error);
      toast.error("Failed to fetch products by type");
      return [];
    } finally {
      setIsItemTypeLoading(false);
    }
  }, []);

  // Load all Regular/BOM products on component mount
  useEffect(() => {
    fetchProductsByItemTypeCategory();
  }, [fetchProductsByItemTypeCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Regular button click
  const handleShowRegularProducts = async () => {
    if (itemTypeFilter === "Regular") {
      // Clear filter
      setItemTypeFilter("");
      await fetchData();
      setCurrentPage(1);
    } else {
      // Apply Regular filter
      setItemTypeFilter("Regular");
      setIsItemTypeLoading(true);
      try {
        const regularProducts =
          await fetchProductsByItemTypeCategory("Regular");
        setFilteredProducts(regularProducts);
        setCurrentPage(1);
      } catch (error) {
        toast.error("Failed to load Regular products");
      } finally {
        setIsItemTypeLoading(false);
      }
    }
  };

  // Handle BOM button click
  const handleShowBomProducts = async () => {
    if (itemTypeFilter === "BOM") {
      // Clear filter
      setItemTypeFilter("");
      await fetchData();
      setCurrentPage(1);
    } else {
      // Apply BOM filter
      setItemTypeFilter("BOM");
      setIsItemTypeLoading(true);
      try {
        const bomProducts = await fetchProductsByItemTypeCategory("BOM");
        setFilteredProducts(bomProducts);
        setCurrentPage(1);
      } catch (error) {
        toast.error("Failed to load BOM products");
      } finally {
        setIsItemTypeLoading(false);
      }
    }
  };

  // Search and filter logic
  useEffect(() => {
    let sourceData = itemTypeFilter ? filteredByItemType : productData;

    const q = searchTerm.toLowerCase();
    let res = sourceData.filter(
      (p) =>
        (p.productName?.toLowerCase() || "").includes(q) ||
        (p.itemCode?.toLowerCase() || "").includes(q) ||
        (p.barCode?.toLowerCase() || "").includes(q),
    );

    if (filterCategory) {
      const fq = filterCategory.toLowerCase();
      res = res.filter(
        (p) =>
          (p.categoryName?.toLowerCase() || "").includes(fq) ||
          (p.brandName?.toLowerCase() || "").includes(fq) ||
          (p.itemTypeName?.toLowerCase() || "").includes(fq),
      );
    }

    setFilteredProducts(res);
    setCurrentPage(1);
  }, [
    searchTerm,
    filterCategory,
    productData,
    filteredByItemType,
    itemTypeFilter,
  ]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Move this product to history?")) return;
    try {
      await axios.put(
        `${domain}/api/Products/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Product archived successfully.");
      fetchData();
    } catch {
      toast.error("Failed to delete product.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Products/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Product restored!");
      fetchData();
    } catch {
      toast.error("Failed to restore product.");
    }
  };

  const handlePrintBarcode = useReactToPrint({
    content: () => printBarcodeRef.current,
    documentTitle: "Product-Barcode",
  });

  const handlePrint = (product) => {
    if (product?.barCode?.length !== 13 || !/^\d{13}$/.test(product.barCode)) {
      toast.error("Barcode must be 13-digit EAN-13 format to print.");
      return;
    }
    setBarcodeToPrint(product);
    setTimeout(handlePrintBarcode, 500);
  };

  const handlePrintSelected = () => {
    setBarcodeToPrint(
      selectedProductsForPrint.map((p) => ({
        barCode: p.barCode,
        productName: p.productName,
      })),
    );
    setTimeout(handlePrintBarcode, 500);
    setPrintModalVisible(false);
  };

  const start = (currentPage - 1) * ITEMS_PER_PAGE;
  const current = filteredProducts.slice(start, start + ITEMS_PER_PAGE);

  const totalProducts = productData.length;
  const totalSerialized = productData.filter((p) => p.hasSerial).length;
  const totalWithBarcode = productData.filter((p) => p.barCode).length;

  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const getAllItemsToSync = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}/api/Products/products-to-sync`);
      const data = res.data.map((item) => ({ ...item, productImage: noImage }));
      
      return data
    } catch {
      return []
    }
  }, []);

  const handleSyncToAccounting = async () => {
    try {
      setIsSyncing(true);

      const _productData = await getAllItemsToSync();

      if (!_productData || _productData.length === 0) {
        toast.error("No items available to sync.");
        return;
      }

      const payload = _productData.map((c) => {
        return {
          itemCode: c.itemCode || "",
          itemDescription: c.productName || "",
          uomName: c.baseUomCode || "",
          purchaseUomName: c.purchaseUomCode || "",
          salesUomName: c.salesUomCode || "",
          brandName: c.brandName || "",
          categoryName: c.categoryName || "",
          isBOM: c.isBom || false,
          itemComponents: c.bomComponents.map((comp) => ({
            componentItemCode: comp.componentItemCode || "",
            uomName: comp.uomName || "",
            quantity: comp.quantity || 0,
          })),
        };
      });

      const endpoint = `${domainAccounting}/api/Item/bulk`;

      await axios.post(endpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        `Successfully synced ${payload.length} products to Accounting!`,
      );
      setIsSyncModalVisible(false);
    } catch (error) {
      console.error("Accounting Sync Error:", error);
      toast.error("Failed to sync products to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── TOP HEADER BAR ── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-xl">
                <Package size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 leading-tight">
                  Product Inventory
                </h1>
                <p className="text-xs text-slate-400 hidden sm:block">
                  Manage items, barcodes &amp; classifications
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Regular Products Button */}
              <button
                onClick={handleShowRegularProducts}
                disabled={isItemTypeLoading}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl shadow-sm transition ${
                  itemTypeFilter === "Regular"
                    ? "bg-indigo-600 text-white border border-indigo-600"
                    : "text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
                } ${isItemTypeLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isItemTypeLoading && itemTypeFilter === "Regular" ? (
                  <Loader size={15} className="animate-spin" />
                ) : (
                  <Package size={15} />
                )}
                <span className="hidden sm:inline">Regular Products</span>
              </button>

              {/* BOM Products Button */}
              <button
                onClick={handleShowBomProducts}
                disabled={isItemTypeLoading}
                className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl shadow-sm transition ${
                  itemTypeFilter === "BOM"
                    ? "bg-purple-600 text-white border border-purple-600"
                    : "text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100"
                } ${isItemTypeLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isItemTypeLoading && itemTypeFilter === "BOM" ? (
                  <Loader size={15} className="animate-spin" />
                ) : (
                  <Layers size={15} />
                )}
                <span className="hidden sm:inline">BOM Products</span>
              </button>

              <button
                onClick={() => setIsHistoryOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <History size={15} />
                <span className="hidden sm:inline">Archive</span>
              </button>
              <button
                onClick={() => setIsBulkUploadOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <FileSpreadsheet size={15} />
                <span className="hidden sm:inline">Bulk Upload</span>
              </button>

              <button
                onClick={() => setIsSyncModalVisible(true)}
                className="flex items-center gap-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <RefreshCw size={18} />
                <span>Sync to Accounting</span>
              </button>

              {/* <button
                onClick={() => setIsBomModalOpen(true)}
                className="flex items-center gap-2 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <Layers size={15} /> Add New BOM
              </button> */}

              <button
                onClick={() => {
                  setProductToEdit(null);
                  setIsModalVisible(true);
                }}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-xl shadow-sm transition"
              >
                <Plus size={15} /> Add Product
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 space-y-5">
        {/* Active filter indicator */}
        {itemTypeFilter && (
          <div className="flex items-center gap-2">
            <div
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                itemTypeFilter === "Regular"
                  ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                  : "bg-purple-100 text-purple-700 border border-purple-200"
              }`}
            >
              Showing {itemTypeFilter} products only
              <button
                onClick={async () => {
                  setItemTypeFilter("");
                  await fetchData();
                  setCurrentPage(1);
                }}
                className="ml-2 hover:opacity-70 inline-flex items-center"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            icon={Package}
            label="Total Products"
            value={totalProducts}
            color="indigo"
          />
          <StatCard
            icon={Barcode}
            label="With Barcode"
            value={totalWithBarcode}
            color="emerald"
          />
          <StatCard
            icon={Tags}
            label="Serialized Items"
            value={totalSerialized}
            color="amber"
          />
        </div>

        {/* Search & Filter bar */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, item code or barcode…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="relative sm:w-64">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Filter by type, brand or category…"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition"
            />
          </div>
          {(searchTerm || filterCategory) && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 flex-shrink-0 self-center">
              <AlertCircle size={13} className="text-amber-400" />
              {filteredProducts.length} of{" "}
              {itemTypeFilter ? filteredByItemType.length : totalProducts} shown
            </div>
          )}
        </div>

        {/* Table */}
        {loading || isItemTypeLoading ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3 text-slate-300">
              <Loader className="animate-spin" size={36} />
              <p className="text-sm">Loading products…</p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Product
                      </th>
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Codes
                      </th>
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Classification
                      </th>
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide text-center">
                        UOM
                      </th>
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide text-center">
                        Type
                      </th>
                      <th className="px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {current.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50/70 transition group"
                      >
                        {/* Product name */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                              <Package size={14} className="text-indigo-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800 text-sm leading-tight">
                                {item.productName}
                              </p>
                              {item.description?.length > 0 && (
                                <button
                                  onClick={() =>
                                    setSelectedDescription(item.description)
                                  }
                                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-500 transition mt-0.5"
                                >
                                  <FileText size={11} /> view desc
                                </button>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Codes */}
                        <td className="px-5 py-3.5">
                          <span className="inline-block font-mono text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                            {item.itemCode}
                          </span>
                          {item.barCode && (
                            <p className="font-mono text-xs text-slate-400 mt-1">
                              {item.barCode}
                            </p>
                          )}
                        </td>

                        {/* Classification: Item Type + Brand + Category */}
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {item.itemTypeName && (
                              <span className="inline-block text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md">
                                {item.itemTypeName}
                              </span>
                            )}
                            {item.brandName && (
                              <span className="inline-block text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md">
                                {item.brandName}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {item.categoryName || (
                              <span className="text-slate-300">
                                Uncategorized
                              </span>
                            )}
                          </p>
                          {item.categoryTwoName && (
                            <p className="text-xs text-slate-400">
                              {item.categoryTwoName}
                            </p>
                          )}
                        </td>

                        {/* UOM */}
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md">
                            {item.baseUomName || "—"}
                          </span>
                        </td>

                        {/* Type badge */}
                        <td className="px-5 py-3.5 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                item.hasSerial
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-slate-100 text-slate-500 border border-slate-200"
                              }`}
                            >
                              {item.hasSerial ? "Serialized" : "Standard"}
                            </span>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                item.nonTrade
                                  ? "bg-orange-50 text-orange-600 border border-orange-200"
                                  : "bg-sky-50 text-sky-600 border border-sky-200"
                              }`}
                            >
                              {item.nonTrade ? "Non-Trade" : "Trade"}
                            </span>
                            {item.activeBomId && (
                              <span
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200 mt-1 uppercase tracking-wide shadow-sm"
                                title={item.activeBomName}
                              >
                                BOM
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setAuditItem(item)}
                              className="p-1.5 text-slate-300 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition"
                              title="Audit trail"
                            >
                              <Clock size={15} />
                            </button>
                            <button
                              onClick={() => handlePrint(item)}
                              className="p-1.5 text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition"
                              title="Print barcode"
                            >
                              <Printer size={15} />
                            </button>
                            <button
                              onClick={() => {
                                setProductToEdit(item);
                                setIsModalVisible(true);
                              }}
                              className="p-1.5 text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Edit size={15} />
                            </button>
                            <button
                              onClick={() => deleteProduct(item.id)}
                              className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                              title="Archive"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {current.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-16 text-center">
                          <div className="flex flex-col items-center gap-2 text-slate-300">
                            <Package size={32} />
                            <p className="text-sm">No products found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {current.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 py-16 flex flex-col items-center gap-2 text-slate-300">
                  <Package size={32} />
                  <p className="text-sm">No products found</p>
                </div>
              ) : (
                current.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Package size={15} className="text-indigo-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 text-sm leading-tight truncate">
                            {item.productName}
                          </p>
                          <p className="font-mono text-xs text-slate-400 mt-0.5">
                            {item.itemCode}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                            item.hasSerial
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {item.hasSerial ? "Serial" : "Std"}
                        </span>
                        {item.activeBomId && (
                          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 uppercase">
                            BOM
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {item.itemTypeName && (
                        <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md">
                          {item.itemTypeName}
                        </span>
                      )}
                      {item.brandName && (
                        <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md">
                          {item.brandName}
                        </span>
                      )}
                      {item.categoryName && (
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                          {item.categoryName}
                        </span>
                      )}
                      {item.baseUomName && (
                        <span className="text-xs text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-md">
                          {item.baseUomName}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      {item.barCode ? (
                        <p className="font-mono text-xs text-slate-400">
                          {item.barCode}
                        </p>
                      ) : (
                        <span className="text-xs text-slate-300">
                          No barcode
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-1.5 text-slate-300 hover:text-amber-500 rounded-lg transition"
                        >
                          <Clock size={15} />
                        </button>
                        <button
                          onClick={() => handlePrint(item)}
                          className="p-1.5 text-slate-300 hover:text-indigo-500 rounded-lg transition"
                        >
                          <Printer size={15} />
                        </button>
                        <button
                          onClick={() => {
                            setProductToEdit(item);
                            setIsModalVisible(true);
                          }}
                          className="p-1.5 text-slate-300 hover:text-indigo-500 rounded-lg transition"
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          onClick={() => deleteProduct(item.id)}
                          className="p-1.5 text-slate-300 hover:text-red-500 rounded-lg transition"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div className="pb-4">
              <Pagination
                itemsPerPage={ITEMS_PER_PAGE}
                totalItems={filteredProducts.length}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>

      {/* ── HIDDEN PRINT AREA ── */}
      <div style={{ display: "none" }}>
        <PrintBarcodeComponent ref={printBarcodeRef} barcode={barcodeToPrint} />
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {isModalVisible && (
        <AddProducts
          onClose={() => {
            setProductToEdit(null);
            setIsModalVisible(false);
          }}
          refreshData={fetchData}
          productToEdit={productToEdit}
        />
      )}

      {/* ── DESCRIPTION MODAL ── */}
      {selectedDescription && (
        <DescriptionModal
          text={selectedDescription}
          onClose={() => setSelectedDescription("")}
        />
      )}

      {/* ── PRINT SELECTION MODAL ── */}
      <PrintSelectionModal
        visible={printModalVisible}
        products={filteredProducts}
        selectedProducts={selectedProductsForPrint}
        onClose={() => setPrintModalVisible(false)}
        onPrint={handlePrintSelected}
        onCheckboxChange={setSelectedProductsForPrint}
      />

      {/* ── HISTORY MODAL ── */}
      <DeletedHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => {
          setIsHistoryOpen(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* ── AUDIT MODAL ── */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.productName}
        rows={buildAuditRows(auditItem)}
      />

      {/* ── BULK UPLOAD ── */}
      {isBulkUploadOpen && (
        <Bulkproductpage
          isOpen={isBulkUploadOpen}
          initialRestoreId={null}
          onClose={() => setIsBulkUploadOpen(false)}
          onSuccess={() => {
            setIsBulkUploadOpen(false);
            fetchData();
            toast.success("Bulk upload complete — list refreshed.");
          }}
        />
      )}

      {/* ── MANAGE BOM MODAL ── */}
      {isBomModalOpen && (
        <ManageBomModal
          onClose={() => {
            setIsBomModalOpen(false);
            fetchData();
          }}
          refreshData={fetchData}
        />
      )}

      <SyncConfirmModal
        isOpen={isSyncModalVisible}
        onClose={() => setIsSyncModalVisible(false)}
        onConfirm={handleSyncToAccounting}
        dataCount={productData.length}
        syncing={isSyncing}
      />
    </div>
  );
};

// ==========================================
// SYNC CONFIRMATION MODAL
// ==========================================
const SyncConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  dataCount,
  syncing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col transform transition-all">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-emerald-50 rounded-full text-emerald-600">
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
            </div>
            Sync to Accounting
          </h2>
          <button
            onClick={onClose}
            disabled={syncing}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition disabled:opacity-50"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 bg-slate-50/50">
          <div className="bg-white p-4 rounded-xl border border-slate-200 mb-2">
            <p className="text-sm text-slate-600 leading-relaxed text-center">
              Are you sure you want to sync <br />
              <strong className="text-xl text-emerald-600">
                {dataCount}
              </strong>{" "}
              <br />
              products to the Accounting system?
            </p>
            <p className="text-xs text-slate-400 text-center mt-3">
              This will securely push all current product data to the
              centralized accounting database.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-5">
            <button
              onClick={onClose}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={syncing}
              className="px-4 py-2 flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-70"
            >
              {syncing ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Confirm Sync"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllProducts;
